import React, { memo, useMemo } from 'react';
import { formatDurationRange } from '../../../../../utils/taskDuration';
import { getTaskEstimatedDurationRange } from '../../../FlowchartView/utils/progressUtils';
import { useTableContext } from '../../context/TableContext';
import { formatDueDateDisplay, formatDueDateFull, parseMissionDate } from '../../../../../utils/missionDates';

/**
 * Format a date for display
 * @param {string|Date} date - The date to format
 * @param {boolean} includeTime - Whether to include time
 * @returns {string} Formatted date string
 */
const formatDate = (date, includeTime = false) => {
  if (!date) return '';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    
    const options = { 
      month: 'short', 
      day: 'numeric',
      year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    };
    
    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }
    
    return d.toLocaleDateString('en-US', options);
  } catch {
    return '';
  }
};

/**
 * Check if a due date is overdue
 * @param {string|Date} date - The due date
 * @returns {boolean} True if overdue
 */
const isOverdue = (date) => {
  if (!date) return false;
  try {
    const d = parseMissionDate(date);
    if (!d) return false;
    return d.getTime() < Date.now();
  } catch {
    return false;
  }
};

/**
 * Check if a due date is today
 * @param {string|Date} date - The due date
 * @returns {boolean} True if today
 */
const isToday = (date) => {
  if (!date) return false;
  try {
    const d = new Date(date);
    const today = new Date();
    return d.toDateString() === today.toDateString();
  } catch {
    return false;
  }
};

/**
 * Custom comparison for date cells
 */
const areDateCellPropsEqual = (dateField) => (prevProps, nextProps) => {
  const prevTask = prevProps.row.original;
  const nextTask = nextProps.row.original;
  return prevTask.id === nextTask.id && 
         prevTask[dateField] === nextTask[dateField] &&
         prevTask.status === nextTask.status;
};

/**
 * DueDateCell - Displays due date with overdue styling
 * Memoized to prevent re-renders when other cells change
 */
export const DueDateCell = memo(({ row }) => {
  const task = row.original;
  const dueDate = task.dueDate;
  
  const { overdue, today, formatted, titleText } = useMemo(() => {
    if (!dueDate) return { overdue: false, today: false, formatted: '', titleText: '' };
    const fd = formatDueDateDisplay(dueDate);
    return {
      overdue: isOverdue(dueDate) && task.status !== 'completed',
      today: isToday(dueDate),
      formatted: fd?.text || '',
      titleText: formatDueDateFull(dueDate),
    };
  }, [dueDate, task.status]);
  
  if (!dueDate) {
    return <span className="table-view__cell-empty">—</span>;
  }

  return (
    <span 
      className={`table-view__date-cell ${overdue ? 'table-view__date-cell--overdue' : ''} ${today ? 'table-view__date-cell--today' : ''}`}
      title={titleText}
    >
      {formatted}
    </span>
  );
}, areDateCellPropsEqual('dueDate'));

/**
 * EstimatedDurationCell - Displays estimated duration (minutes-based)
 */
export const EstimatedDurationCell = memo(({ row }) => {
  const task = row.original;
  const { statuses, nestedSprints, allSprints } = useTableContext();
  const range = useMemo(
    () => getTaskEstimatedDurationRange(task, { statuses, nestedSprints, allSprints }),
    [task, statuses, nestedSprints, allSprints],
  );

  if (range.maxMinutes == null) {
    return <span className="table-view__cell-empty">—</span>;
  }

  const hasRange = task.nestedSprintId && (range.minMinutes ?? 0) !== range.maxMinutes;
  const title = task.nestedSprintId
    ? (hasRange
      ? 'Fastest to longest route through open choices'
      : 'Total estimated time from missions in nested campaign')
    : undefined;

  return (
    <span className="table-view__days-cell" title={title}>
      {formatDurationRange(range.minMinutes ?? 0, range.maxMinutes)}
    </span>
  );
});

/**
 * CreatedAtCell - Displays created date
 * Memoized to prevent re-renders when other cells change
 */
export const CreatedAtCell = memo(({ row }) => {
  const task = row.original;
  const date = task.createdAt;
  
  const { formatted, titleText } = useMemo(() => {
    if (!date) return { formatted: '', titleText: '' };
    return {
      formatted: formatDate(date),
      titleText: new Date(date).toLocaleString(),
    };
  }, [date]);
  
  if (!date) {
    return <span className="table-view__cell-empty">—</span>;
  }

  return (
    <span className="table-view__date-cell" title={titleText}>
      {formatted}
    </span>
  );
}, areDateCellPropsEqual('createdAt'));

/**
 * StartedAtCell - Displays start date
 * Memoized to prevent re-renders when other cells change
 */
export const StartedAtCell = memo(({ row }) => {
  const task = row.original;
  const date = task.startedAt;
  
  const { formatted, titleText } = useMemo(() => {
    if (!date) return { formatted: '', titleText: '' };
    return {
      formatted: formatDate(date),
      titleText: new Date(date).toLocaleString(),
    };
  }, [date]);
  
  if (!date) {
    return <span className="table-view__cell-empty">—</span>;
  }

  return (
    <span className="table-view__date-cell" title={titleText}>
      {formatted}
    </span>
  );
}, areDateCellPropsEqual('startedAt'));

/**
 * CompletedAtCell - Displays completed date
 * Memoized to prevent re-renders when other cells change
 */
export const CompletedAtCell = memo(({ row }) => {
  const task = row.original;
  const date = task.completedAt;
  
  const { formatted, titleText } = useMemo(() => {
    if (!date) return { formatted: '', titleText: '' };
    return {
      formatted: formatDate(date),
      titleText: new Date(date).toLocaleString(),
    };
  }, [date]);
  
  if (!date) {
    return <span className="table-view__cell-empty">—</span>;
  }

  return (
    <span className="table-view__date-cell table-view__date-cell--completed" title={titleText}>
      {formatted}
    </span>
  );
}, areDateCellPropsEqual('completedAt'));

/**
 * UpdatedAtCell - Displays last updated date
 * Memoized to prevent re-renders when other cells change
 */
export const UpdatedAtCell = memo(({ row }) => {
  const task = row.original;
  const date = task.updatedAt;
  
  const { formatted, titleText } = useMemo(() => {
    if (!date) return { formatted: '', titleText: '' };
    return {
      formatted: formatDate(date, false),
      titleText: new Date(date).toLocaleString(),
    };
  }, [date]);
  
  if (!date) {
    return <span className="table-view__cell-empty">—</span>;
  }

  return (
    <span className="table-view__date-cell" title={titleText}>
      {formatted}
    </span>
  );
}, areDateCellPropsEqual('updatedAt'));
